import React from 'react';

interface UseInfiniteScrollPaginationParams<T extends object> {
  items: T[];
  limit: number;
}

interface UseInfiniteScrollPaginationPayload<T extends object> {
  paginatedItems: T[];
  onIntersect: () => void;
  doneIntersecting: boolean;
}

export const useInfiniteScrollPagination = <T extends object>({
  items,
  limit,
}: UseInfiniteScrollPaginationParams<T>): UseInfiniteScrollPaginationPayload<T> => {
  const [offset, setOffset] = React.useState<number>(limit);

  const paginatedItems = React.useMemo(() => {
    return items.slice(0, offset);
  }, [items, offset]);

  const doneIntersecting = React.useMemo(
    () => offset >= items.length,
    [offset, items.length],
  );

  const onIntersect = React.useCallback(() => {
    if (doneIntersecting) {
      return;
    }

    setOffset((offset) => {
      const numToIncrement = Math.min(limit, items.length - offset);

      return offset + numToIncrement;
    });
  }, [limit, items.length, doneIntersecting]);

  return {
    paginatedItems,
    onIntersect,
    doneIntersecting,
  };
};
